import {
  type JSX,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Controller } from "react-hook-form";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Button,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
  useZodForm,
} from "@fluid-app/ui-primitives";
import type { fluidPay } from "@fluid-app/fluid-pay-core";
import { useFluidPayApi } from "@fluid-app/fluid-pay-core";
import {
  useVgsCollect,
  type VgsFieldState,
  type VgsFormState,
} from "../hooks/use-vgs-collect";

const SENTINEL_CREATE_NEW = "__create_new__";

const formSchema = z.object({
  billing_address_selection: z
    .string()
    .refine((value) => value !== "" && value !== SENTINEL_CREATE_NEW, {
      message: "Please select a billing address",
    }),
  set_as_default: z.boolean(),
});

type FormData = z.infer<typeof formSchema>;

export interface CreditCardFormSubmitData {
  set_as_default: boolean;
  payment_method: {
    token: string;
    cvv_token: string;
    exp_month: string;
    exp_year: string;
    card_holder: string;
    bin?: string;
    last_four?: string;
    card_network?: string;
  };
  billing_address_id: number;
}

export interface RenderAddressFormDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onAddressCreated: (address: fluidPay.CustomerAddress) => void;
}

interface CreditCardFormDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreditCardFormSubmitData) => void;
  isSubmitting: boolean;
  error?: string;
  jwt: string;
  /** When provided, adds a top "Same as shipping" option referencing this address. */
  shippingAddress?: fluidPay.CustomerAddress | null;
  /** Saved customer addresses populating the dropdown. */
  savedAddresses?: fluidPay.CustomerAddress[];
  /** ID of the customer's preferred billing address; used to pre-select. */
  defaultBillingAddressId?: number;
  /**
   * Render-prop slot for AddressFormDialog. The host wires the dialog so this
   * component doesn't import it directly, keeping the address-create flow's
   * data plumbing (mutations, error toasts) under host control.
   */
  renderAddressFormDialog?: (props: RenderAddressFormDialogProps) => ReactNode;
  t: (key: string) => string;
}

function VgsInputWrapper({
  id,
  label,
  fieldState,
  className,
}: {
  id: string;
  label: string;
  fieldState?: VgsFieldState;
  className?: string;
}) {
  const isInvalid =
    fieldState?.isValid === false &&
    !fieldState?.isFocused &&
    !fieldState?.isEmpty;
  const isFocused = !!fieldState?.isFocused;

  return (
    <div
      id={id}
      role="group"
      aria-label={label}
      data-invalid={isInvalid ? "" : undefined}
      className={cn(
        "border-input selection:bg-primary selection:text-primary-foreground file:text-foreground placeholder:text-muted-foreground h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
        "data-[invalid]:border-destructive data-[invalid]:ring-destructive/20",
        "[&_iframe]:block! [&_iframe]:h-full! [&_iframe]:w-full! [&_iframe]:border-0!",
        isFocused && "border-ring ring-ring/50 ring-[3px]",
        isFocused && isInvalid && "border-destructive ring-destructive/20",
        className,
      )}
    />
  );
}

function VgsCardForm({
  vgsFieldState,
  networkError,
}: {
  vgsFieldState: VgsFormState;
  networkError?: string;
}) {
  return (
    <div className="space-y-4" aria-label="Credit card form">
      <VgsInputWrapper
        id="vgs-card-number"
        label="Card number"
        fieldState={vgsFieldState["card_number"]}
      />

      <div className="flex gap-3">
        <div className="flex-1">
          <VgsInputWrapper
            id="vgs-expiration-date"
            label="Expiration date"
            fieldState={vgsFieldState["card_exp"]}
          />
        </div>
        <div className="flex-1">
          <VgsInputWrapper
            id="vgs-cvc"
            label="CVC"
            fieldState={vgsFieldState["card_cvc"]}
          />
        </div>
      </div>

      <VgsInputWrapper
        id="vgs-card-holder"
        label="Cardholder name"
        fieldState={vgsFieldState["card_holder"]}
      />

      {networkError && networkError.trim().length > 0 && (
        <div className="bg-destructive/10 text-destructive rounded-md p-3 text-sm">
          {networkError}
        </div>
      )}
    </div>
  );
}

function formatAddressOneLine(addr: fluidPay.CustomerAddress): string {
  const cityStateZip = [addr.city, addr.state, addr.postal_code]
    .filter((p): p is string => Boolean(p && p.trim().length > 0))
    .join(", ");
  const parts = [
    addr.address1,
    addr.address2,
    cityStateZip,
    addr.country_code,
  ].filter((p): p is string => Boolean(p && p.trim().length > 0));
  return parts.join(" • ");
}

function AddressSummaryCard({
  address,
}: {
  address: fluidPay.CustomerAddress;
}) {
  return (
    <div className="border-input bg-muted/30 rounded-md border p-3 text-sm break-words">
      {address.name && <div className="font-medium">{address.name}</div>}
      <div className="text-muted-foreground">{address.address1}</div>
      {address.address2 && (
        <div className="text-muted-foreground">{address.address2}</div>
      )}
      <div className="text-muted-foreground">
        {[address.city, address.state].filter(Boolean).join(", ")}
        {address.postal_code ? ` ${address.postal_code}` : ""}
      </div>
      {address.country_code && (
        <div className="text-muted-foreground">{address.country_code}</div>
      )}
    </div>
  );
}

export default function CreditCardFormDialog({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
  error,
  jwt,
  shippingAddress,
  savedAddresses = [],
  defaultBillingAddressId,
  renderAddressFormDialog,
  t,
}: CreditCardFormDialogProps): JSX.Element {
  const api = useFluidPayApi();
  const [cardError, setCardError] = useState<string | undefined>(undefined);
  const [isVgsSubmitting, setIsVgsSubmitting] = useState(false);
  const [createdAddresses, setCreatedAddresses] = useState<
    fluidPay.CustomerAddress[]
  >([]);
  const [isAddressDialogOpen, setIsAddressDialogOpen] = useState(false);

  const {
    isFormReady,
    isLoading: isVgsLoading,
    isScriptLoaded,
    scriptLoadError,
    initializeForm,
    submitCard,
    vgsFieldState,
    resetVgs,
    onSuccessRef,
    onErrorRef,
  } = useVgsCollect();

  const shippingId = shippingAddress?.id ?? null;
  const dedupedSavedAddresses = useMemo(
    () =>
      shippingId === null
        ? savedAddresses
        : savedAddresses.filter((a) => a.id !== shippingId),
    [savedAddresses, shippingId],
  );

  const allAddresses = useMemo<fluidPay.CustomerAddress[]>(() => {
    const list: fluidPay.CustomerAddress[] = [];
    if (shippingAddress) list.push(shippingAddress);
    list.push(...dedupedSavedAddresses);
    list.push(...createdAddresses);
    return list;
  }, [shippingAddress, dedupedSavedAddresses, createdAddresses]);

  const hasRealAddresses = allAddresses.length > 0;

  // Default selection per ticket: shipping → default billing → first saved → "use different".
  const initialSelection = useMemo<string>(() => {
    if (shippingAddress) return shippingAddress.id.toString();
    if (
      defaultBillingAddressId &&
      dedupedSavedAddresses.some((a) => a.id === defaultBillingAddressId)
    ) {
      return defaultBillingAddressId.toString();
    }
    const first = dedupedSavedAddresses[0];
    if (first) return first.id.toString();
    return SENTINEL_CREATE_NEW;
  }, [shippingAddress, defaultBillingAddressId, dedupedSavedAddresses]);

  const { control, handleSubmit, reset, setValue, watch } =
    useZodForm<FormData>(formSchema, {
      defaultValues: {
        billing_address_selection: initialSelection,
        set_as_default: false,
      },
    });

  // Reset the form whenever the dialog (re)opens or the upstream address list
  // changes — the initial selection depends on shipping/saved/default props.
  useEffect(() => {
    if (isOpen) {
      reset({
        billing_address_selection: initialSelection,
        set_as_default: false,
      });
      setCreatedAddresses([]);
    }
  }, [isOpen, initialSelection, reset]);

  const currentSelection = watch("billing_address_selection");
  const setAsDefault = watch("set_as_default");

  const selectedAddress = useMemo(() => {
    if (!currentSelection || currentSelection === SENTINEL_CREATE_NEW) {
      return null;
    }
    const id = Number(currentSelection);
    if (!Number.isFinite(id)) return null;
    return allAddresses.find((a) => a.id === id) ?? null;
  }, [currentSelection, allAddresses]);

  const selectOptions = useMemo(() => {
    const options: { name: string; value: string }[] = [];
    if (shippingAddress) {
      options.push({
        name: t("same_as_shipping"),
        value: shippingAddress.id.toString(),
      });
    }
    for (const addr of dedupedSavedAddresses) {
      options.push({
        name: formatAddressOneLine(addr),
        value: addr.id.toString(),
      });
    }
    for (const addr of createdAddresses) {
      options.push({
        name: formatAddressOneLine(addr),
        value: addr.id.toString(),
      });
    }
    options.push({
      name: t("use_a_different_address"),
      value: SENTINEL_CREATE_NEW,
    });
    return options;
  }, [shippingAddress, dedupedSavedAddresses, createdAddresses, t]);

  const handleSelectionChange = useCallback(
    (value: string) => {
      if (value === SENTINEL_CREATE_NEW) {
        // Don't commit the sentinel to form state — opening the sub-modal is
        // the action. The form's previous selection remains intact so closing
        // the sub-modal without saving leaves the dropdown unchanged.
        setIsAddressDialogOpen(true);
        return;
      }
      setValue("billing_address_selection", value, { shouldValidate: true });
    },
    [setValue],
  );

  const handleAddressCreated = useCallback(
    (address: fluidPay.CustomerAddress) => {
      setCreatedAddresses((list) => [...list, address]);
      setValue("billing_address_selection", address.id.toString(), {
        shouldValidate: true,
      });
      setIsAddressDialogOpen(false);
    },
    [setValue],
  );

  const handleVgsSuccess = useCallback(
    (response: fluidPay.VgsCollectSubmitResponse) => {
      const expValue = response?.card_exp;
      if (!expValue) {
        setCardError("Missing expiration date from payment form");
        setIsVgsSubmitting(false);
        return;
      }

      const cleanExp = expValue.replace(/\s+/g, "");
      const [expMonth, expYearShort] = cleanExp.split("/");

      if (!expMonth || !expYearShort) {
        setCardError("Invalid expiration date format");
        setIsVgsSubmitting(false);
        return;
      }

      const expYear =
        expYearShort.length === 2 ? `20${expYearShort}` : expYearShort;

      const cardholderName = response?.card_holder?.trim();
      if (!cardholderName) {
        setCardError("Cardholder name is required");
        setIsVgsSubmitting(false);
        return;
      }

      handleSubmit(
        (formData) => {
          const billingAddressId = Number(formData.billing_address_selection);
          if (!Number.isFinite(billingAddressId)) {
            setCardError("Please select a billing address.");
            setIsVgsSubmitting(false);
            return;
          }

          const cardMeta = vgsFieldState["card_number"];
          onSubmit({
            set_as_default: formData.set_as_default,
            payment_method: {
              token: response.card_number,
              cvv_token: response.card_cvc,
              exp_month: expMonth,
              exp_year: expYear,
              card_holder: cardholderName,
              ...(cardMeta?.bin ? { bin: cardMeta.bin } : {}),
              ...(cardMeta?.last4 ? { last_four: cardMeta.last4 } : {}),
              ...(cardMeta?.cardType
                ? { card_network: cardMeta.cardType }
                : {}),
            },
            billing_address_id: billingAddressId,
          });
          setIsVgsSubmitting(false);
        },
        () => {
          setCardError("Please select a billing address.");
          setIsVgsSubmitting(false);
        },
      )();
    },
    [handleSubmit, onSubmit, vgsFieldState],
  );

  const handleVgsError = useCallback((err: string) => {
    setCardError(err);
    setIsVgsSubmitting(false);
  }, []);

  useEffect(() => {
    onSuccessRef.current = handleVgsSuccess;
    onErrorRef.current = handleVgsError;
  }, [handleVgsSuccess, handleVgsError, onSuccessRef, onErrorRef]);

  useEffect(() => {
    if (!isOpen || !isScriptLoaded) return;

    let cancelled = false;

    const init = async () => {
      try {
        const credentials = await api.vault.fetchCredentials(jwt);
        if (!cancelled) {
          await initializeForm(credentials);
        }
      } catch (err) {
        console.error("Failed to fetch vault credentials:", err);
        if (!cancelled) {
          setCardError("Failed to initialize payment form");
        }
      }
    };

    init();

    return () => {
      cancelled = true;
    };
  }, [isOpen, isScriptLoaded, jwt, api, initializeForm]);

  const handleClose = () => {
    resetVgs();
    reset();
    setCardError(undefined);
    setIsVgsSubmitting(false);
    setIsAddressDialogOpen(false);
    onClose();
  };

  const handleFormSubmit = handleSubmit(() => {
    setIsVgsSubmitting(true);
    setCardError(undefined);
    submitCard();
  });

  const isBusy = isSubmitting || isVgsSubmitting;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
        <DialogContent className="max-h-[90vh] max-w-md overflow-y-auto p-0 md:max-w-xl">
          <div className="relative min-w-0 p-6">
            {(isVgsLoading || !isFormReady) && !scriptLoadError && (
              <div className="bg-background/80 absolute inset-0 z-10 flex items-center justify-center rounded backdrop-blur-[1px]">
                <div className="flex items-center justify-center">
                  <div className="border-primary mr-3 size-5 animate-spin rounded-full border-2 border-t-2 border-t-transparent" />
                  <p className="text-muted-foreground text-sm font-medium">
                    {t("securing")}
                  </p>
                </div>
              </div>
            )}
            <DialogHeader>
              <DialogTitle>{t("add_credit_card")}</DialogTitle>
            </DialogHeader>

            <div className="space-y-4 pt-6">
              <VgsCardForm
                vgsFieldState={vgsFieldState}
                networkError={cardError ?? scriptLoadError ?? error}
              />

              <div className="space-y-3 pt-2">
                <h3 className="text-muted-foreground text-sm font-medium">
                  {t("billing_address")}
                </h3>
                {hasRealAddresses ? (
                  <Controller
                    control={control}
                    name="billing_address_selection"
                    render={({
                      field: { value },
                      fieldState: { error: fieldError },
                    }) => (
                      <div className="space-y-1">
                        <Label
                          htmlFor="billing-address-selection"
                          className="mb-1.5 block text-sm font-medium"
                        >
                          {t("billing_address")}
                        </Label>
                        <Select
                          value={value}
                          onValueChange={handleSelectionChange}
                          disabled={isBusy}
                        >
                          <SelectTrigger
                            id="billing-address-selection"
                            aria-invalid={!!fieldError}
                            className="w-full min-w-0 [&>span]:block! [&>span]:min-w-0 [&>span]:truncate"
                          >
                            <SelectValue placeholder={t("select_an_option")} />
                          </SelectTrigger>
                          <SelectContent>
                            {selectOptions.map((opt) => (
                              <SelectItem
                                key={opt.value}
                                value={opt.value}
                                // Radix Select fires onValueChange only when the value
                                // *changes*. Picking the already-selected sentinel
                                // would otherwise be a no-op, leaving the user with
                                // no way to reopen the address form.
                                onClick={
                                  opt.value === SENTINEL_CREATE_NEW
                                    ? () => setIsAddressDialogOpen(true)
                                    : undefined
                                }
                              >
                                {opt.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {fieldError?.message && (
                          <p className="text-destructive text-sm">
                            {fieldError.message}
                          </p>
                        )}
                      </div>
                    )}
                  />
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsAddressDialogOpen(true)}
                    disabled={isBusy}
                    className="w-full"
                  >
                    {t("add_billing_address")}
                  </Button>
                )}

                {selectedAddress && (
                  <AddressSummaryCard address={selectedAddress} />
                )}
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between">
              <div className="flex items-center gap-x-2">
                <input
                  type="checkbox"
                  id="set_as_default_cc"
                  checked={setAsDefault}
                  onChange={() => setValue("set_as_default", !setAsDefault)}
                  disabled={isBusy}
                  className="size-4"
                />
                <label htmlFor="set_as_default_cc" className="text-sm">
                  {t("set_as_default_payment_method")}
                </label>
              </div>

              <Button
                type="button"
                onClick={handleFormSubmit}
                disabled={isBusy}
              >
                {isBusy && (
                  <div className="mr-2 size-4 animate-spin rounded-full border-2 border-current/30 border-t-current" />
                )}
                {isBusy ? t("saving") : t("save_card")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {renderAddressFormDialog?.({
        isOpen: isAddressDialogOpen,
        onClose: () => setIsAddressDialogOpen(false),
        onAddressCreated: handleAddressCreated,
      })}
    </>
  );
}
